import { makeStyles } from '@material-ui/styles'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Info from 'core/components/validatedForm/Info'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal'
import Text from 'core/elements/Text'
import useDeleteAction from 'core/hooks/useDeleteAction'
import useListAction from 'core/hooks/useListAction'
import Theme from 'core/themes/model'
import { getCurrentUser } from 'openstack/helpers'
import { deletePort } from '../networks/actions'
import {
  deleteVolumeSnapshot,
  getVolumeSnapshots,
  listVolumeSnapshots,
} from '../storage/snapshots/actions'
import { volumeSnapshotsSelector } from '../storage/snapshots/selectors'
import { deleteVirtualMachine, enableTerminationForVolumes } from './actions'
import checkboxStyles from './checkboxStyles'
import DetailedDeleting from './deleteVmDialog/DetailedDeleting'
import DetailedResults from './deleteVmDialog/DetailedResults'
import DetailedSelection from './deleteVmDialog/DetailedSelection'
import deleteVmStyles from './deleteVmDialog/deleteVmStyles'
import {
  deleteSnapshotsAndWait,
  getVmPorts,
  getVmVolumes,
  initiateResourcesDeletion,
  initiateSimpleVmDeletion,
} from './deleteVmDialog/helpers'
import RootView from './deleteVmDialog/RootView'
import SimpleDeleting from './deleteVmDialog/SimpleDeleting'
import SimpleResults from './deleteVmDialog/SimpleResults'
import SimpleResultsDeleteAll from './deleteVmDialog/SimpleResultsDeleteAll'
import { hasOtherUserVm } from './helpers'

enum TaskState {
  Root,
  DeletingSimple,
  ResultsSimple,
  DeletingAllSimple,
  ResultsAllSimple,
  SelectDetailed,
  DeletingDetailed,
  ResultsDetailed,
}

const panelForTask: Record<TaskState, 'dialog' | 'drawer'> = {
  [TaskState.Root]: 'dialog',
  [TaskState.DeletingSimple]: 'dialog',
  [TaskState.ResultsSimple]: 'dialog',
  [TaskState.DeletingAllSimple]: 'dialog',
  [TaskState.ResultsAllSimple]: 'dialog',
  [TaskState.SelectDetailed]: 'drawer',
  [TaskState.DeletingDetailed]: 'drawer',
  [TaskState.ResultsDetailed]: 'drawer',
}

const useStyles = makeStyles<Theme>((theme) => ({
  ...checkboxStyles(theme),
  ...deleteVmStyles(theme),
}))

const DeleteVirtualMachineDialog = ({ rows, onClose }) => {
  const classes = useStyles()
  const { role: userRole, userId } = getCurrentUser()
  const [currentTask, setCurrentTask] = useState<TaskState>(TaskState.Root)
  const [deleteAssociatedResources, setDeleteAssociatedResources] = useState(false)
  const [volumesToDelete, setVolumesToDelete] = useState([])
  const [portsToDelete, setPortsToDelete] = useState([])
  const [vmDeletionResults, setVmDeletionResults] = useState([])
  const [volumeDeletionResults, setVolumeDeletionResults] = useState([])
  const [portDeletionResults, setPortDeletionResults] = useState([])
  const [haveVolumeSnapshotsToDelete, setHaveVolumeSnapshotsToDelete] = useState(false)

  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots)
  const volumeSnapshots = useSelector(volumeSnapshotsSelector)

  const { bulkRemove } = useDeleteAction(deleteVirtualMachine)
  const { bulkRemove: bulkRemovePorts } = useDeleteAction(deletePort)
  const { bulkRemove: bulkRemoveSnapshots } = useDeleteAction(deleteVolumeSnapshot)

  const vmVolumes = useMemo(() => {
    return getVmVolumes(rows)
  }, [rows])

  const vmPorts = useMemo(() => {
    return getVmPorts(rows)
  }, [rows])

  const handleClose = useCallback(
    (success?: boolean) => {
      setCurrentTask(TaskState.Root)
      onClose(success)
    },
    [onClose],
  )

  const goToDetailedView = useCallback(() => {
    setCurrentTask(TaskState.SelectDetailed)
    // Honor each resource's "Delete on Termination" setting: only pre-select
    // resources already marked for deletion. Resources set to be kept start
    // unselected so the user does not accidentally delete them (PCD-8814).
    setVolumesToDelete(vmVolumes.filter((volume) => volume?.delete_on_termination))
    setPortsToDelete(vmPorts.filter((port) => port?.delete_on_termination))
  }, [vmVolumes, vmPorts])

  // Delete all snapshots of delete_on_termination volumes
  // so that volume deletion goes through
  const deleteDependentSnapshots = useCallback(
    async (additionalVolumeIds = []) => {
      await deleteSnapshotsAndWait({
        vmVolumes,
        volumeSnapshots,
        additionalVolumeIds,
        setHaveVolumeSnapshotsToDelete,
        bulkRemoveSnapshots,
        getVolumeSnapshots,
      })
    },
    [vmVolumes, volumeSnapshots],
  )

  const normalDelete = useCallback(async () => {
    setCurrentTask(TaskState.DeletingSimple)
    await initiateSimpleVmDeletion({
      virtualMachines: rows,
      deleteDependentSnapshots,
      bulkRemove,
      setVmDeletionResults,
    })
    setCurrentTask(TaskState.ResultsSimple)
  }, [rows, deleteDependentSnapshots])

  const deleteAll = useCallback(async () => {
    setCurrentTask(TaskState.DeletingAllSimple)
    // For tracking intended deletions on detailed results
    setVolumesToDelete(vmVolumes)
    setPortsToDelete(vmPorts)
    await initiateResourcesDeletion({
      virtualMachines: rows,
      vmVolumes,
      vmPorts,
      deleteDependentSnapshots,
      bulkRemove,
      bulkRemovePorts,
      enableTerminationForVolumes,
      setVmDeletionResults,
      setVolumeDeletionResults,
      setPortDeletionResults,
    })
    setCurrentTask(TaskState.ResultsAllSimple)
  }, [rows, vmVolumes, vmPorts, deleteDependentSnapshots])

  const deleteSelectedResources = useCallback(async () => {
    setCurrentTask(TaskState.DeletingDetailed)
    await initiateResourcesDeletion({
      virtualMachines: rows,
      vmVolumes: volumesToDelete,
      vmPorts: portsToDelete,
      deleteDependentSnapshots,
      bulkRemove,
      bulkRemovePorts,
      enableTerminationForVolumes,
      setVmDeletionResults,
      setVolumeDeletionResults,
      setPortDeletionResults,
    })
    setCurrentTask(TaskState.ResultsDetailed)
  }, [rows, volumesToDelete, portsToDelete, deleteDependentSnapshots])

  const failedVmDeletions = useMemo(() => {
    return vmDeletionResults.filter((result) => !result.success)
  }, [vmDeletionResults])
  const successfulVmDeletions = useMemo(() => {
    return vmDeletionResults.filter((result) => result.success)
  }, [vmDeletionResults])
  const allVmSuccess = !failedVmDeletions?.length

  const failedVolumeDeletions = useMemo(() => {
    return volumeDeletionResults.filter((result) => !result.success)
  }, [volumeDeletionResults])
  const successfulVolumeDeletions = useMemo(() => {
    return volumeDeletionResults.filter((result) => result.success)
  }, [volumeDeletionResults])
  const allVolumeSuccess = !failedVolumeDeletions?.length

  const failedPortDeletions = useMemo(() => {
    return portDeletionResults.filter((result) => !result.success)
  }, [portDeletionResults])
  const successfulPortDeletions = useMemo(() => {
    return portDeletionResults.filter((result) => result.success)
  }, [portDeletionResults])
  const allPortSuccess = !failedPortDeletions?.length

  const allResourcesSuccess = allVmSuccess && allVolumeSuccess && allPortSuccess

  return (
    <Modal
      panel={panelForTask[currentTask]}
      open
      onClose={handleClose}
      title={
        <div className={classes.titleContainer}>
          <Text variant="h4" component="header">
            {rows?.length === 1
              ? 'Delete 1 Virtual Machine'
              : `Delete ${rows.length} Virtual Machines`}
          </Text>
          {[
            TaskState.SelectDetailed,
            TaskState.DeletingDetailed,
            TaskState.ResultsDetailed,
          ].includes(currentTask) && (
            <div className={classes.subTitleContainer}>
              <Text variant="body2">
                VMs: <b>{rows?.length}</b>
              </Text>
              <div className={classes.verticalDivider} />
              <Text variant="body2">
                Selected volumes: <b>{volumesToDelete?.length}</b>
              </Text>
              <div className={classes.verticalDivider} />
              <Text variant="body2">
                Selected ports: <b>{portsToDelete?.length}</b>
              </Text>
            </div>
          )}
        </div>
      }
      maxWidth={panelForTask[currentTask] === 'dialog' ? 600 : 880}
      // footerButtonAlignment="normal"
      footer={
        currentTask === TaskState.Root ? (
          <div className={classes.spaceBetween}>
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (deleteAssociatedResources) {
                  deleteAll()
                } else {
                  normalDelete()
                }
              }}
            >
              Delete
            </Button>
          </div>
        ) : currentTask === TaskState.SelectDetailed ? (
          <div className={classes.spaceBetween}>
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button onClick={() => deleteSelectedResources()}>Delete VMs & Resources</Button>
          </div>
        ) : [
            TaskState.DeletingSimple,
            TaskState.DeletingAllSimple,
            TaskState.DeletingDetailed,
          ].includes(currentTask) ? (
          <>
            <Button variant="secondary" disabled>
              Close
            </Button>
          </>
        ) : [TaskState.ResultsSimple, TaskState.ResultsAllSimple].includes(currentTask) ? (
          <div className={classes.spaceBetween}>
            <Button variant="secondary" onClick={() => setCurrentTask(TaskState.ResultsDetailed)}>
              Show Details
            </Button>
            <Button onClick={() => handleClose(true)}>Close</Button>
          </div>
        ) : (
          <>
            <Button variant="secondary" onClick={() => handleClose()}>
              Close
            </Button>
          </>
        )
      }
    >
      {currentTask === TaskState.Root && (
        <>
          {userRole === 'member' && hasOtherUserVm({ vms: rows, userId }) && (
            <Info error>NOTE: You are performing an operation on another user's VM.</Info>
          )}
          <RootView
            vmVolumes={vmVolumes}
            vmPorts={vmPorts}
            goToDetailedView={goToDetailedView}
            deleteAssociatedResources={deleteAssociatedResources}
            setDeleteAssociatedResources={setDeleteAssociatedResources}
          />
        </>
      )}
      {currentTask === TaskState.DeletingSimple && (
        <SimpleDeleting
          haveVolumeSnapshotsToDelete={haveVolumeSnapshotsToDelete}
          deleteAll={false}
        />
      )}
      {currentTask === TaskState.ResultsSimple && (
        <SimpleResults
          virtualMachines={rows}
          allVmSuccess={allVmSuccess}
          successfulVmDeletions={successfulVmDeletions}
          vmDeletionResults={vmDeletionResults}
        />
      )}
      {currentTask === TaskState.DeletingAllSimple && (
        <SimpleDeleting
          haveVolumeSnapshotsToDelete={haveVolumeSnapshotsToDelete}
          deleteAll={true}
        />
      )}
      {currentTask === TaskState.ResultsAllSimple && (
        <SimpleResultsDeleteAll
          virtualMachines={rows}
          vmVolumes={vmVolumes}
          vmPorts={vmPorts}
          allResourcesSuccess={allResourcesSuccess}
          vmDeletionResults={vmDeletionResults}
          successfulVmDeletions={successfulVmDeletions}
          successfulVolumeDeletions={successfulVolumeDeletions}
          successfulPortDeletions={successfulPortDeletions}
        />
      )}
      {currentTask === TaskState.SelectDetailed && (
        <DetailedSelection
          vmVolumes={vmVolumes}
          volumesToDelete={volumesToDelete}
          setVolumesToDelete={setVolumesToDelete}
          vmPorts={vmPorts}
          portsToDelete={portsToDelete}
          setPortsToDelete={setPortsToDelete}
        />
      )}
      {currentTask === TaskState.DeletingDetailed && (
        <DetailedDeleting
          haveVolumeSnapshotsToDelete={haveVolumeSnapshotsToDelete}
          volumesToDelete={volumesToDelete}
          portsToDelete={portsToDelete}
        />
      )}
      {currentTask === TaskState.ResultsDetailed && (
        <DetailedResults
          virtualMachines={rows}
          vmVolumes={vmVolumes}
          vmPorts={vmPorts}
          allVmSuccess={allVmSuccess}
          allVolumeSuccess={allVolumeSuccess}
          allPortSuccess={allPortSuccess}
          successfulVmDeletions={successfulVmDeletions}
          vmDeletionResults={vmDeletionResults}
          volumesToDelete={volumesToDelete}
          portsToDelete={portsToDelete}
          volumeDeletionResults={volumeDeletionResults}
          portDeletionResults={portDeletionResults}
          failedVmDeletions={failedVmDeletions}
          failedVolumeDeletions={failedVolumeDeletions}
          failedPortDeletions={failedPortDeletions}
        />
      )}
    </Modal>
  )
}

export default DeleteVirtualMachineDialog
